<template>
  <transition appear name="from-below">

    <v-row @wheel="onMouseWheel" no-gutters class="gallery mb-10"  :class="{'expanded-diplay-mode': displayMode === 'expanded', 'compact-display-mode' : displayMode === 'compact', 'invert-ui': invertUi || !!data.name}" >
      <div class="gallery-title" v-if="data.name">{{data.name}}</div>

      <template v-if="controls">
        <template v-if="pageNumbers">
          <div class="selected-image">{{(selectedImage + 1).toString().padStart(2, '0')}}</div>
          <div class="total-images">/ {{(galleryArray.length).toString().padStart(2, '0')}}</div>
        </template>
        <v-btn
          data-cursor-target="center center"
          :class="{deselected: hoverNextPrev === 1}"
          @mouseenter.native="hoverNextPrev = 0"
          @mouseleave.native="hoverNextPrev = -1"
          :disabled="selectedImage === 0"
          icon
          class="go-prev-image"
          @click="selectedImage = Math.max(0, Math.min(galleryArray.length - 1, selectedImage - 1))"
          aria-label="Previous"
          >
          <ArrowLeft />
        </v-btn>
        <v-btn
          data-cursor-target="center center"
          :class="{deselected: hoverNextPrev === 0}"
          @mouseenter.native="hoverNextPrev = 1"
          @mouseleave.native="hoverNextPrev = -1"
          :disabled="selectedImage === galleryArray.length - 1"
          icon
          class="go-next-image"
          @click="selectedImage = Math.max(0, Math.min(galleryArray.length - 1, selectedImage + 1))"
          aria-label="Next"
          >
          <ArrowRight />
        </v-btn>
      </template>

      <transition><div :key="selectedImage" v-if="captions && captions[selectedImage] && $vuetify.breakpoint.lgAndUp" class="caption" :style="{left: `${20}%`}">{{captions[selectedImage]}}</div></transition>
      <v-row
        class="flex-nowrap scroller"
        :class="{snap: !mousedown}"
        :style="{height: `${componentHeight}px`}"
        ref="slider"
        @mouseleave="onMouseLeave"

        @mousedown="onMouseDown"
        @touchstart="onMouseDown"

        @mouseup="onMouseUp"
        @click.capture="onMouseClick"
        @touchend="onMouseUp"

        @mousemove.prevent="onMouseMove"
        @touchmove="onMouseMove"

        >
        <v-col
          :cols="itemsCols"
          ref="image"
          v-for="(galleryElement, i) in galleryArray"
          :key="i"
          class="scroll-child fill-height"
          >
          <template v-if="galleryElement.mimeType && galleryElement.mimeType.indexOf('image/') === 0">
            <slot name="image" :galleryElement="galleryElement"  >
              <seo-v-img
                class="image-scroll-child"
                :src="`/media/${galleryElement.id}`"
                :lazy-src="JSON.parse(galleryElement.metaData)['image-lazy-src']"
                contain
                width="100%"
                height="100%"
                @mousedown.native="onGalleryElementMouseDown($event, galleryElement)"
                @toushstart.native="onGalleryElementMouseDown($event, galleryElement)"
                @mouseup.native="onGalleryElementMouseUp($event, galleryElement)"
                @touchend.native="onGalleryElementMouseUp($event, galleryElement)"
              />
            </slot>
          </template>
          <template v-else-if="galleryElement.mimeType && galleryElement.mimeType.indexOf('video/') === 0">
            <slot name="video" :galleryElement="galleryElement">
              <v-responsive
                width="100%"
                height="100%"
                @mousedown.native="onGalleryElementMouseDown($event, galleryElement)"
                @toushstart.native="onGalleryElementMouseDown($event, galleryElement)"
                @mouseup.native="onGalleryElementMouseUp($event, galleryElement)"
                @touchend.native="onGalleryElementMouseUp($event, galleryElement)"
                >
                <video
                  :style="{height: '100%', width: '100%', cursor: 'pointer'}"
                  muted
                  playsinline
                  autoplay
                  loop
                  >
                  <source :src="`/media/${galleryElement.id}`" :type="galleryElement.mimeType">
                </video>
              </v-responsive>
            </slot>
          </template>
          <template v-else>
            <slot name="generic" :galleryElement="galleryElement" :onGalleryElementMouseDown="onGalleryElementMouseDown" :onGalleryElementMouseUp="onGalleryElementMouseUp">
              <div />
            </slot>
          </template>
        </v-col>
        <template v-if="!noSpaceRight">
          <v-col class="scroll-child" :cols="itemsCols"><v-responsive width="100%" :height="viewPort.height * 0.5" /></v-col>
          <v-col class="scroll-child" :cols="itemsCols"><v-responsive width="100%" :height="viewPort.height * 0.5" /></v-col>
          <v-col class="scroll-child" :cols="itemsCols"><v-responsive width="100%" :height="viewPort.height * 0.5" /></v-col>
          <v-col class="scroll-child" :cols="itemsCols"><v-responsive width="100%" :height="viewPort.height * 0.5" /></v-col>
        </template>
      </v-row>

      <v-dialog v-model="fullscreenOpen" :overlay-opacity="0.9" @keydown="onFullScreenKey" content-class="fullscreen-gallery-element" >
        <transition mode="out-in" :name="lastFullScreenElementIndex > fullscreenElementIndex ? 'scroll-x-transition': 'scroll-x-reverse-transition'">
          <div
            v-touch="{left: nextFullScreenElement, right: prevFullScreenElement}"
            :key="fullscreenElement.id"
            ref="fullScreenElementContainer"
            v-if="fullscreenElement && fullscreenElement.mimeType && fullscreenElement.mimeType.indexOf('image/') === 0"
            @click="closeFullscreen"
            class="fullscreen-element-container"
            >
            <slot name="fullscreen-image" :fullscreenElement="fullscreenElement" >
              <seo-v-img
                :key="fullscreenElement.id"
                :src="`/media/${fullscreenElement.id}`"
                :lazy-src="JSON.parse(fullscreenElement.metaData)['image-lazy-src']"
                :aspect-ratio="parseInt(JSON.parse(fullscreenElement.metaData)['image-width']) / parseInt(JSON.parse(fullscreenElement.metaData)['image-height'])"
                contain
                height="100%"
                width="100%"
                />
            </slot>
          </div>
          <div
            :key="fullscreenElement.id"
            ref="fullScreenElementContainer"
            @click="closeFullscreen"
            v-touch="{left: nextFullScreenElement, right: prevFullScreenElement}"
            v-else-if="fullscreenElement && fullscreenElement.mimeType && fullscreenElement.mimeType.indexOf('video/') === 0"
            class="fullscreen-element-container"
            >
            <slot name="fullscreen-video" :fullscreenElement="fullscreenElement">
              <v-responsive height="100%" width="100%" :key="fullscreenElement.id">
                <video class="fullscreen-video" controls autoplay :key="fullscreenElement.id" :style="{maxWidth: '100%', maxHeight: '100%'}">
                  <source :src="`/media/${fullscreenElement.id}`" :type="fullscreenElement.mimeType">
                </video>
              </v-responsive>
            </slot>
          </div>
          <div
            v-else
            :key="fullscreenElement ? fullscreenElement.id : ''"
            ref="fullScreenElementContainer"
            v-touch="{left: nextFullScreenElement, right: prevFullScreenElement}"
            class="fullscreen-element-container"
          >
            <slot name="fullscreen-generic" :fullscreenElement="fullscreenElement" ></slot>
          </div>
        </transition>
        <transition><div :key="fullscreenElement.id" v-if="captions && captions[galleryArray.indexOf(fullscreenElement)] && $vuetify.breakpoint.mdAndDown" class="fullscreen-caption">{{captions[galleryArray.indexOf(fullscreenElement)]}}</div></transition>
        <v-btn
          data-cursor-target="center center"
          @mouseenter="hoverNextPrev = 0"
          @mouseleave="hoverNextPrev = -1"
          text
          dark
          class="fullscreen-nav-btn py-16 fullscreen-nav-btn--prev"
          :disabled="galleryArray.indexOf(fullscreenElement) === 0"
          @click="prevFullScreenElement"
          :class="{deselected: hoverNextPrev === 1}"
          :style="{marginLeft: '-23px', paddingLeft: `${fullscreenElementScreenWidth}% !important`}"
          aria-label="Previous"
          >
            <ArrowLeft />
          </v-btn>
        <v-btn
          data-cursor-target="center center"
          @mouseenter="hoverNextPrev = 1"
          @mouseleave="hoverNextPrev = -1"
          text
          dark
          class="fullscreen-nav-btn py-16 fullscreen-nav-btn--next"
          :disabled="galleryArray.indexOf(fullscreenElement) === galleryArray.length - 1"
          @click="nextFullScreenElement"
          :class="{deselected: hoverNextPrev === 0}"
          :style="{marginRight: '-23px', paddingRight: `${fullscreenElementScreenWidth}% !important`}"
          aria-label="Next"
          >
            <ArrowRight />
          </v-btn>
      </v-dialog>
    </v-row>
  </transition>
</template>

<script>
import { Viewport } from '@monogrid/vue-lib'
import { ElasticNumber, RAF, normalizeWheel } from '@monogrid/js-utils'
import delay from 'lodash/delay'
import ArrowRight from '@/assets/img/chevron-right.svg'
import ArrowLeft from '@/assets/img/chevron-left.svg'

export default {
  name: 'ContentGallery2',
  mixins: [Viewport],
  components: {
    ArrowLeft,
    ArrowRight
  },
  data () {
    return {
      mousedown: false,
      startX: 0,
      scrollLeft: 0,
      selectedImage: 0,
      walk: 0,
      oldWalk: 0,
      hoverNextPrev: -1,
      fullscreenElement: null,
      fullscreenOpen: false,
      fullscreenElementScreenWidth: 0,
      lastFullScreenElementIndex: 0,
      scrollValue: new ElasticNumber(0)
    }
  },
  props: {
    data: {
      type: [Object, Array],
      required: true
    },
    captions: {
      type: Array,
      required: false
    },
    controls: {
      type: Boolean,
      required: false,
      default: true
    },
    noSpaceRight: {
      type: Boolean,
      required: false,
      default: false
    },
    pageNumbers: {
      type: Boolean,
      required: false,
      default: true
    },
    wheel: {
      type: Boolean,
      required: false,
      default: false
    },
    displayMode: {
      type: String,
      required: false,
      validator: (prop) => [
        'expanded',
        'compact',
        'full'
      ].includes(prop),
      default: 'expanded'
    },
    height: {
      type: Number,
      required: false,
      default () {
        return -1
      }
    },
    cols: {
      type: Number,
      required: false,
      default () {
        return -1
      }
    },
    invertUi: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    fullscreenElementIndex () {
      return this.galleryArray.indexOf(this.fullscreenElement)
    },
    // fullscreenElementScreenWidth () {
    //   const element = this.galleryArray[this.fullscreenElementIndex]
    //   if (element) {
    //     const meta = JSON.parse(element.metaData)
    //     console.log(meta)
    //   }
    //   return 0
    // },
    componentHeight () {
      if (this.height !== -1) return this.height
      return this.viewPort.height * 0.5
    },
    galleryArray () {
      if (Array.isArray(this.data)) return this.data
      return this.data.images
    },
    itemsCols () {
      if (this.cols !== -1) return this.cols
      switch (this.displayMode) {
        case 'expanded':
          return 8
        case 'compact':
          return 5
        case 'full':
          return this.$vuetify.breakpoint.mdAndUp ? 3 : 5
      }
      return 8
    }
  },
  mounted () {
    this.scrollValue.speed = 10
    this.time = new Date().getTime()
    RAF.add(this.onUpdate)
  },
  destroyed () {
    RAF.remove(this.onUpdate)
  },
  watch: {
    viewPort () {
      this.calcFullscreenImageWidth()
    },
    fullscreenElementIndex (value) {
      delay(this.calcFullscreenImageWidth, 100)
    },
    fullscreenOpen (value) {
      if (!value) {
        delay(() => {
          this.fullscreenElement = null
        }, 300)
      } else {
        delay(() => {
          const els = document.getElementsByClassName('fullscreen-gallery-element')
          els[els.length - 1].parentNode.focus()
        }, 300)
      }
    },
    fullscreenElement (value) {
      if (value) {
        this.selectedImage = this.galleryArray.indexOf(value)
      }
    },
    data () {
      this.scrollValue.value = this.scrollValue.target = 0
    },
    selectedImage (value) {
      this.onSelectedImageChange(value)
    }
  },
  methods: {
    calcFullscreenImageWidth () {
      const element = this.galleryArray[this.fullscreenElementIndex]
      if (element && this.$refs.fullScreenElementContainer && element.mimeType.indexOf('image/') === 0) {
        const meta = JSON.parse(element.metaData)
        const imageWidth = parseFloat(meta['image-width'])
        const imageHeight = parseFloat(meta['image-height'])
        const bounds = this.$refs.fullScreenElementContainer.getBoundingClientRect()

        const imageRatio = imageWidth / imageHeight
        const boundsRatio = bounds.width / bounds.height

        let realImageWidth = bounds.width

        if (imageRatio < boundsRatio) {
          // meta.w : meta.h = x : bounds.h
          realImageWidth = (bounds.height * parseFloat(meta['image-width'])) / parseFloat(meta['image-height'])
        }
        const diff = (bounds.width - realImageWidth) / 2

        // console.log('content width calculated:', bounds.width)
        this.fullscreenElementScreenWidth = (diff / bounds.width) * 100
      } else {
        if (element && element.mimeType.indexOf('image/') === 0 && !this.$refs.fullScreenElementContainer) {
          // retry
          // console.log('retrying')
          delay(this.calcFullscreenImageWidth, 30)
        } else {
          // console.log('no info on content width')
          this.fullscreenElementScreenWidth = 0
        }
      }
    },
    closeFullscreen (e) {
      this.fullscreenOpen = false
      delay(() => {
        this.fullscreenElement = null
      }, 300)
    },
    onFullScreenKey (e) {
      if (e.key === 'ArrowRight') {
        this.nextFullScreenElement()
      } else if (e.key === 'ArrowLeft') {
        this.prevFullScreenElement()
      }
    },
    nextFullScreenElement () {
      if (this.fullscreenElementIndex < this.galleryArray.length - 1) {
        this.lastFullScreenElementIndex = this.fullscreenElementIndex
        this.fullscreenElement = this.galleryArray[this.fullscreenElementIndex + 1]
      }
    },
    prevFullScreenElement () {
      if (this.fullscreenElementIndex > 0) {
        this.lastFullScreenElementIndex = this.fullscreenElementIndex
        this.fullscreenElement = this.galleryArray[this.fullscreenElementIndex - 1]
      }
    },
    onMouseClick (e) {
      // console.log('mouse click')
      if (Math.abs(this.oldWalk) > 50) {
        // console.log('stop mouse click')
        e.preventDefault()
        e.stopPropagation()
      }
    },
    onMouseWheel (e) {
      // if (!this.wheel) return
      // console.log('ok')
      const evt = normalizeWheel(e)
      if (evt.pixelX !== 0) e.preventDefault()
      this.scrollValue.target += this.wheel ? evt.pixelY + evt.pixelX : (event.pixelX || 0)
      if (this.wheelTimeout) clearTimeout(this.wheelTimeout)
      this.wheelTimeout = delay(this.onMouseUp, 100)
    },
    onGalleryElementMouseDown (e, image) {
      this.imageClickStart = e.touches && e.touches.length > 0 ? e.touches[0].pageX : e.pageX
    },
    onGalleryElementMouseUp (e, image) {
      const imageClickEnd = e.touches && e.touches.length > 0 ? e.touches[0].pageX : e.pageX
      if (Math.abs(imageClickEnd - this.imageClickStart) < 10) {
        this.fullscreenElement = image
        this.fullscreenOpen = true
        this.lastFullScreenElementIndex = this.fullscreenElementIndex
      }
    },
    onImageClick (image) {
    },
    onUpdate () {
      const now = new Date().getTime()
      const delta = (now - this.time) / 1000

      if (this.noSpaceRight && this.$refs.image && this.$refs.slider) {
        let width = 0
        let lastWidth = 0
        this.$refs.image.forEach(image => {
          lastWidth = image.getBoundingClientRect().width
          width += lastWidth
        })
        width -= this.$refs.slider.getBoundingClientRect().width
        this.scrollValue.target = Math.max(0, Math.min(width, this.scrollValue.target))
      }

      this.scrollValue.update(Math.min(delta, 1 / 10))
      this.$refs.slider.scrollLeft = this.scrollValue.value
      this.time = now
    },
    onMouseDown (e) {
      this.mousedown = true
      // this.startX = e.pageX - this.$refs.slider.offsetLeft
      this.startX = e.touches && e.touches.length > 0 ? e.touches[0].pageX : e.pageX
      this.scrollLeft = this.scrollValue.target
    },
    onMouseMove (e) {
      if (!this.mousedown) return
      this.walk = (e.touches && e.touches.length > 0 ? e.touches[0].pageX : e.pageX) - this.startX
      this.scrollValue.target = this.scrollLeft - this.walk

      if (Math.abs(this.walk) > 50) {
        e.preventDefault()
        // e.stopPropagation()
      }
      // console.log('scrollvalue.target', this.scrollValue.target)
      // if (isNaN(this.scrollValue.target)) {
      //   this.scrollValue.target = 0
      //   console.log('ooops')
      // }
      // console.log(this.walk)
    },
    onSelectedImageChange (value) {
      // console.log('Selected Image', value)
      if (!this.$refs.slider) return
      this.scrollValue.target = this.$refs.slider.getBoundingClientRect().width * (this.itemsCols / 12) * value

      // this.walk = 0

      // if (isNaN(this.scrollValue.target)) {
      //   this.scrollValue.target = 0
      //   console.log('ooops')
      // }
    },

    onMouseLeave (e) {
      this.startX = e.touches && e.touches.length > 0 ? e.touches[0].pageX : e.pageX
      this.scrollLeft = this.scrollValue.target
      this.onMouseUp(e)
    },

    onMouseUp (e) {
      this.mousedown = false
      let width = 0
      this.$refs.image.forEach(image => {
        width += image.getBoundingClientRect().width
      })
      // width -= this.$refs.slider.getBoundingClientRect().width * 0.5

      const newSelectedImage = Math.floor(this.galleryArray.length * (this.scrollValue.target / width))
      if (newSelectedImage !== this.selectedImage) {
        // console.log('Selected image', newSelectedImage, this.scrollValue.target)
        this.selectedImage = newSelectedImage
      } else {
        if (Math.abs(this.walk) > 50) {
          this.selectedImage = this.selectedImage - (this.walk > 0 ? 1 : -1)
          e.preventDefault()
          e.stopPropagation()
        }
      }
      this.selectedImage = Math.max(0, Math.min(this.galleryArray.length - 1, this.selectedImage))
      // console.log('Selected image final', this.selectedImage, this.scrollValue.target)
      this.oldWalk = this.walk
      this.walk = 0
      this.onSelectedImageChange(this.selectedImage)
    }
  }
}
</script>

<style lang="scss" scoped>
.gallery {
  position: relative;

  &.invert-ui {
    background-color: $c-light-pink;
  }
}

.go-prev-image,
.go-next-image {
  position: absolute;
  top: 90%;
  transform: translateY(-50%);
  z-index: 1;
  font-size: 1.5rem;

  // path {
  //   stroke: map-get($shades, 'black');
  // }

  &.deselected,
  &.v-btn--disabled {
    opacity: 0.2;
    pointer-events: all;
  }

  svg {
    width: 15px;
    height: 15px;
  }

  .invert-ui & {
    // color: map-get($shades, 'white');
    mix-blend-mode: difference;

    path {
      stroke: map-get($shades, 'white');
    }
  }
}

.go-prev-image {
  right: 200px;

  @include breakpoint('sm-and-down') {
    right: 40px;
  }

  .compact-display-mode & {
    right: 80px;

    @include breakpoint('sm-and-down') {
      right: 40px;
    }
  }
}

.go-next-image {
  right: 160px;

  @include breakpoint('sm-and-down') {
    right: 0;
  }

  .compact-display-mode & {
    right: 20px;

    @include breakpoint('sm-and-down') {
      right: 0;
    }
  }
}

.selected-image,
.total-images {
  position: absolute;
  top: 90%;
  pointer-events: none;
  user-select: none;
  transform: translateY(-50%);
  font-family: 'Domaine Sans Display', sans-serif;
  z-index: 1;

  .expanded-diplay-mode.invert-ui & {
    color: map-get($shades, 'white');
    mix-blend-mode: difference;
  }

  .compact-display-mode & {
    left: 30px;
    width: 500px;
    white-space: nowrap;
    transform: translateY(0) translateX(-100%);
    text-align: right;
    font-size: 10rem;
  }

}

.selected-image {
  left: 50px;
  font-size: 2rem;

  @include breakpoint('sm-and-down') {
    left: initial;
    font-size: 2rem;
    display: inline-block;
  }

  .compact-display-mode & {
    top: 20%;
    color: $c-pink;

    @include breakpoint('sm-and-down') {
      top: 85%;
      left: 20px;
      font-size: 2rem;
      display: inline-block;
    }
  }
}

.total-images {
  right: 250px;
  font-size: 4rem;
  opacity: 0.7;

  @include breakpoint('sm-and-down') {
    left: 45px;
    right: initial;
    font-size: 3rem;
    display: inline-block;
  }

  .compact-display-mode & {
    top: 45%;
    opacity: 0.5;

    @include breakpoint('sm-and-down') {
      top: 83%;
      left: 120px;
      right: initial;
      font-size: 3rem;
      display: inline-block;
    }
  }
}

.gallery-title {
  position: absolute;
  top: 0;
  transform: rotate(-180deg);
  z-index: 1;
  text-align: center;
  height: 100%;
  left: 0;
  pointer-events: none;
  writing-mode: vertical-rl;
  text-orientation: mixed;

  .backgroundblendmode & {
    mix-blend-mode: difference;
    color: #fff;
  }
}

.scroller {
  overflow: hidden;
  // overflow-x: auto;
  position: relative;
  cursor: grabbing;
  // scroll-snap-type: x mandatory;

  &.snap {
    cursor: grab;
  }
}

.fullscreen-video {
  height: 100%;
  left: 50%;
  transform: translateX(-50%);
  position: relative;
}

.caption {
  position: absolute;
  bottom: -50px;
  // left: 33%;
  transform: translate(-50%, 0);
  z-index: 1;
  font-size: 1rem !important;
  max-width: 30%;
  text-align: center;
}

.fullscreen-caption {
  position: absolute;
  bottom: 5px;
  left: 50%;
  width: 100%;
  transform: translate(-50%, 0);
  font-size: 1rem !important;
  text-align: center;
  color: #fff;
  mix-blend-mode: difference;
}

.image-scroll-child {
  cursor: pointer;
}

.fullscreen-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: auto;
  z-index: 999;

  // @include breakpoint('xl-only') {
  //   padding-left: 80px !important;
  //   padding-right: 80px !important;
  // }
  // @include breakpoint('lg-and-down') {
  //   padding-left: 50px !important;
  //   padding-right: 50px !important;
  // }
  // @include breakpoint('sm-and-down') {
  //   padding-left: 30px !important;
  //   padding-right: 30px !important;
  // }
  // @include breakpoint('xs-only') {
  //   padding-left: 10px !important;
  //   padding-right: 10px !important;
  // }

  &.deselected {
    opacity: 0.5;
  }

  &--prev {
    left: 10px;
  }

  &--next {
    right: 10px;
  }

  svg {
    width: 20px;
    height: 20px;

    g {
      stroke: currentColor;
    }
  }
}

// dialog top level
::v-deep {
  .fullscreen-gallery-element {
    overflow: hidden;
    position: relative;
    height: 100% !important;
    max-height: initial !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    margin: 0 !important;
  }
}
// container for fullscreen item, not including arrows or mobile caption
.fullscreen-element-container {
  position: relative;
  top: 24px;
  height: calc(100% - 48px);
  width: calc(100% - 48px);
  margin: 0 auto;
}

// .fullscreen-element-enter,
// .fullscreen-element-leave-to {
//   transform: translate3d(-50%, 0, 0);
// }

// .fullscreen-element-enter-active,
// .fullscreen-element-leave-active {
//   transition: transform
// }

</style>
